import { CrownOutlined } from "@ant-design/icons";
import { Avatar, Badge, Button, Modal, Statistic, Tooltip } from "antd";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { ReactComponent as ChartIcon } from "assets/images/chart-icon.svg";
import { reviewBusinessApplication } from "services/SauceService";
import { ReactComponent as TicketIcon } from "assets/images/ticket-icon.svg";
import { ReactComponent as TimerIcon } from "assets/images/timer-icon.svg";
import { ReactComponent as CalendarIcon } from "assets/images/calendar-icon.svg";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { ReactComponent as CloseCircleIcon } from "assets/images/close-circle-icon.svg";
import { ReactComponent as RightArrowleIcon } from "assets/images/chevron-right-icon.svg";
import NotFoundImage from "assets/images/not-found.jpeg";
import "./InfluencerCard.scss";
import ModalCollabTimeSlot from "../../components/modal-collab-time-slot/ModalCollabTimeSlot";
import { useState, useMemo } from "react";
import RescheduleRequest from "components/reschedule-request/RescheduleRequest";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { UserState } from "redux-context/user";
import { Influencer } from "interfaces/influencer";
import { canRescheduleAccepted } from "helpers/reschedule-helper";
import { BusinessState } from "redux-context/business";
import { SubscriptionType } from "enums/SubscriptionType";
import { usePaywall } from "components/paywall/paywallContext";
import { PaywallSource } from "components/paywall/Paywall";
const { Countdown } = Statistic;

const PALETTE = [
  "#F6A623",
  "#E04A00",
  "#FF5C00",
  "#52c41a",
  "#faad14",
  "#ff4d4f",
  "#1677ff",
  "#1f1f1f",
  "#434343",
  "#8c8c8c",
  "#d1a1a1ff",
];

const hexToRgba = (hex: string, alpha = 1) => {
  const h = hex.replace("#", "");
  const bigint = parseInt(
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h,
    16,
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface InfluencerCardProps {
  influencer: Influencer;
  onInfluencerClick: (influencer?: Influencer) => void;
  hideLocation?: boolean;
  index?: number;
  refetch?: () => void;
}

export const formatNumberKMBT = (number: number, fixed: number = 1) => {
  if (!number && number !== 0) return "-";
  if (number > 999 && number <= 999999) {
    return (number / 1000).toFixed(fixed) + "K";
  } else if (number > 999999 && number <= 999999999) {
    return (number / 1000000).toFixed(fixed) + "M";
  } else if (number > 999999999 && number <= 999999999999) {
    return (number / 1000000000).toFixed(fixed) + "B";
  } else if (number > 999999999999) {
    return (number / 1000000000000).toFixed(fixed) + "T";
  } else {
    return number;
  }
};

export const ImgWithErrorHandling: React.FC<any> = ({ src, ...rest }) => {
  const [imgError, setImgError] = useState(false);

  const handleError = () => {
    setImgError(true);
    return true;
  };

  return imgError || !src ? (
    <img src={NotFoundImage} {...rest} alt="" />
  ) : (
    <img src={src} onError={handleError} {...rest} alt="" />
  );
};

const InfluencerCard: React.FC<InfluencerCardProps> = (props) => {
  const {
    influencer,
    hideLocation,
    onInfluencerClick,
    refetch,
    index = 0,
  } = props;
  const [openModalTimeSlot, setOpenModalTimeSlot] = useState<boolean>(false);
  const now = dayjs();
  const { openPaywall } = usePaywall();
  const endDate = dayjs.utc(influencer?.dueDateToReview || null);
  const animationDelay = `${Math.min(index * 60, 600)}ms`;

  const { loggedUser } = useSelector((state: UserState) => state.user);
  const { subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;
  const mustardUser = loggedUser?.email?.includes("@mustard.love");

  const [platform, setPlatform] = useState<"ig" | "tt">(() => {
    const ig = influencer?.instagramFollowersCount ?? 0;
    const tt = influencer?.tiktokFollowersCount ?? 0;
    return ig >= tt ? "ig" : "tt";
  });

  const onClickReject = async (evt: any) => {
    evt.stopPropagation();

    Modal.confirm({
      content: "Are you sure you want to reject this influencer?",
      icon: "",
      onOk: async () => {
        await reviewBusinessApplication({
          bookingId: influencer?.bookingId,
          collabId: influencer?.collabId,
          status: "reject",
        });

        refetch?.();
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const bookDate = influencer?.formatDate
    ? dayjs.utc(influencer?.formatDate.date || "").format("DD MMM YYYY")
    : influencer?.requestDates && influencer?.requestDates.length > 0
      ? dayjs.utc(influencer?.requestDates[0]?.date || "").format("DD MMM YYYY")
      : "";

  const bookTime = influencer?.formatDate
    ? influencer?.formatDate?.hour
    : influencer?.requestDates && influencer?.requestDates.length > 0
      ? influencer?.requestDates[0]?.hour
      : "";

  const onClickAccept = (evt: any) => {
    evt.stopPropagation();
    if (freemium) {
      openPaywall({
        source: PaywallSource.BookingActions,
        onSubscribe: () => setOpenModalTimeSlot(true),
      });
      return;
    }
    setOpenModalTimeSlot(true);
  };

  const onClickCard = (evt: any) => {
    evt.stopPropagation();
    if (openModalTimeSlot) return;
    onInfluencerClick(influencer);
  };

  const classColorByEngagementRate = (engagementRate: number) => {
    if (engagementRate < 1) return "low";
    if (engagementRate < 2) return "medium";
    return "high";
  };

  const colorIndexForTag = (tag: string) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = (hash << 5) - hash + tag.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % PALETTE.length;
  };

  const colorStyleForTag = (tag: string) => {
    const color = PALETTE[colorIndexForTag(tag)];
    return {
      backgroundColor: hexToRgba(color, 0.12),
      color: color,
    } as React.CSSProperties;
  };

  const topImages = influencer?.top3PostsCoverImgUrl?.slice(0, 3) ?? [];
  while (topImages.length < 3) {
    topImages.push(`https://via.placeholder.com/400x300?text=Post`);
  }

  const stats = useMemo(() => {
    const ttStats = (influencer as any).tiktokStats;

    const followers =
      platform === "ig"
        ? (influencer?.instagramFollowersCount ?? 0)
        : (influencer?.tiktokFollowersCount ?? 0);

    const engagement =
      platform === "ig"
        ? (influencer?.engagementRate ?? 0)
        : (ttStats?.engagementRate ?? 0);

    return { followers, engagement };
  }, [platform, influencer]);

  return (
    <div
      key={influencer?.bookingId}
      className="influencer-card"
      style={{ animationDelay }}
    >
      <ModalCollabTimeSlot
        open={openModalTimeSlot}
        onCancel={() => setOpenModalTimeSlot(false)}
        requestDates={influencer.requestDates || []}
        influencer={influencer}
      />
      {influencer?.venue?.name && !hideLocation && (
        <div className="venue text-strong-small">
          <LocationIcon /> {influencer?.venue?.name}
          {" - "}
          {influencer?.venue?.addressDisplay}
        </div>
      )}
      {influencer?.tabStatus === "accepted" && (
        <div
          className="venue display"
          title={`${dayjs
            .utc(influencer?.applicationDate?.date || null)
            .format("MMM DD YYYY")} ${influencer?.applicationDate?.hour}`}
        >
          <CalendarIcon />{" "}
          {dayjs
            .utc(influencer?.applicationDate?.date || null)
            .format("MMM DD YYYY")}
          {" At "} {influencer?.applicationDate?.hour}
        </div>
      )}

      <div className="influencer-card-top">
        <div className="influencer-card-images">
          {(influencer?.top3PostsCoverImgUrl?.length || 0) > 0 ? (
            influencer?.top3PostsCoverImgUrl?.map((src, idx) => (
              <div
                key={idx}
                className={`influencer-card-image influencer-card-image-${idx}`}
              >
                <ImgWithErrorHandling src={src} alt={`post-${idx}`} />
              </div>
            ))
          ) : (
            <div>
              <p
                style={{
                  padding: 20,
                  color: "#999999",
                  textAlign: "center",
                }}
              >
                Influencer profile is being enriched
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="influencer-card-body">
        <div className="influencer-card-header" onClick={onClickCard}>
          <Badge
            count={
              influencer?.isPremium ? (
                <CrownOutlined
                  style={{
                    background: "#faad14",
                    color: "#1D2023",
                    borderRadius: "50%",
                    padding: 4,
                    fontSize: 12,
                  }}
                />
              ) : (
                ""
              )
            }
            color="#faad14"
            offset={[-15, 10]}
          >
            <Avatar
              src={influencer?.avatarUrl}
              size={48}
              className={`influencer-card-avatar ${
                influencer?.isPremium ? "premium" : ""
              }`}
              alt={influencer?.name ?? "avatar"}
            />
          </Badge>

          <div className="influencer-card-info" onClick={onClickCard}>
            <div className="influencer-card-username">
              <span
                className="influencer-card-username-text"
                title={influencer?.name ?? "Influencer"}
              >
                {influencer?.name ?? "Influencer"}
              </span>
              <RightArrowleIcon
                className="influencer-card-username-icon"
                style={{ height: 12, width: 12 }}
              />
            </div>
            <div
              className="influencer-card-tags"
              onScroll={(e) => e.stopPropagation()}
            >
              {influencer?.categories?.slice(0, 6)?.map((t: string) => (
                <span
                  key={t}
                  className="influencer-card-tag"
                  style={colorStyleForTag(t)}
                >
                  #{t}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div
          className="influencer-card-social"
          onClick={(e) => e.stopPropagation()}
          role="tablist"
          aria-label="Platform selector"
        >
          <div className="influencer-card-buttons">
            {(influencer?.instagramFollowersCount || 0) > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPlatform("ig");
                }}
                aria-pressed={platform === "ig"}
                aria-label="Instagram"
                className={`influencer-card-social-btn influencer-card-social-btn-ig ${
                  platform === "ig" ? "is-active" : ""
                }`}
              >
                <InstagramIcon fill="#db2777" />
                <span className="influencer-card-social-text">IG</span>
              </button>
            )}
            {(influencer?.tiktokFollowersCount || 0) > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPlatform("tt");
                }}
                aria-pressed={platform === "tt"}
                aria-label="TikTok"
                className={`influencer-card-social-btn influencer-card-social-btn-tt ${
                  platform === "tt" ? "is-active" : ""
                }`}
              >
                <TikTokIcon stroke="#000" fill="#000" />
                <span className="influencer-card-social-text">TT</span>
              </button>
            )}
          </div>
        </div>

        {stats.followers > 0 && (
          <div className="influencer-card-stats">
            <div className="influencer-card-stat">
              <div className="influencer-card-stat-value">
                {formatNumberKMBT(stats.followers)}
              </div>
              <div className="influencer-card-stat-label">Followers</div>
            </div>
            {stats.engagement > 0 && (
              <div className="influencer-card-stat">
                <div className="influencer-card-stat-value">
                  <ChartIcon
                    className={classColorByEngagementRate(stats.engagement)}
                    style={{ width: 16, height: 16, marginRight: 4 }}
                  />
                  {stats.engagement ? `${stats.engagement}%` : "-"}
                </div>
                <div className="influencer-card-stat-label">Engagement</div>
              </div>
            )}
          </div>
        )}

        {influencer?.tabStatus === "accepted" && (
          <>
            <div className="influencer-card-accepted-info">
              <TicketIcon /> {influencer?.securityCode} {" ∘ "}
              {influencer?.foodCompAmount}
              {" credit "}
              {influencer?.guestAllowed && "(guest allowed)"}
            </div>
            {influencer?.venue?.ghostKitchen && (
              <>
                <div className="influencer-card-ghost-kitchen">
                  <div>
                    <b>Delivery address:</b> {influencer?.deliveryAddress}
                  </div>
                  <div>
                    <b>Date:</b> {`${bookDate} - ${bookTime}`}
                  </div>
                </div>
              </>
            )}
            {canRescheduleAccepted(influencer) && (
              <div className="influencer-card-reschedule">
                <RescheduleRequest influencer={influencer} />
              </div>
            )}
          </>
        )}

        {influencer?.tabStatus === "pending" && (
          <>
            {influencer?.bookingStatus === 2 ? (
              <div className="influencer-card-pending-text">
                Pending influencer accept
              </div>
            ) : (
              <>
                <div className="influencer-card-actions">
                  {influencer?.deliveryTeamApproval && !mustardUser ? (
                    <div className="influencer-card-pending-text">
                      ✨ Pending AI Approval
                    </div>
                  ) : (
                    <>
                      <Button
                        type="primary"
                        className="influencer-card-accept-btn"
                        onClick={onClickAccept}
                      >
                        <span>Accept</span>
                        {!endDate.isValid() || endDate.isBefore(now) ? (
                          "Expiring soon"
                        ) : (
                          <Countdown
                            value={dayjs(influencer?.dueDateToReview)
                              .toDate()
                              .toISOString()}
                            format="HH:mm:ss"
                            prefix={<TimerIcon />}
                            className="influencer-card-countdown"
                          />
                        )}
                      </Button>
                      <Tooltip title="Click to decline this application">
                        <Button
                          icon={<CloseCircleIcon />}
                          onClick={onClickReject}
                          className="influencer-card-reject-btn"
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
                {!influencer?.deliveryTeamApproval && (
                  <div className="influencer-card-reschedule">
                    <RescheduleRequest influencer={influencer} escrowEnabled />
                  </div>
                )}
                {influencer?.venue?.ghostKitchen && (
                  <div className="influencer-card-ghost-kitchen">
                    <div>
                      <b>Delivery address:</b> {influencer?.deliveryAddress}
                    </div>
                    <div>
                      <b>Date:</b> {`${bookDate} - ${bookTime}`}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {influencer?.tabStatus === "expired" &&
          !influencer?.deliveryTeamApproval && (
            <div className="influencer-card-reschedule">
              <RescheduleRequest influencer={influencer} escrowEnabled />
            </div>
          )}
      </div>
    </div>
  );
};

export default InfluencerCard;
